import { useEffect, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Whether the user asked the OS to limit animation.
 *
 * Read by `MediaCarousel` to swap the sliding track for a stacked cross-fade —
 * change without travel. Deliberately **not** used to stop the rotation: the
 * preview carousels carry no dots, badge or swipe, so the rotation is the only
 * way the other outputs are ever seen, and holding on the first one hides
 * content rather than calming it. The setting is about motion, and it covers
 * more people than it looks — iOS reports reduce-motion in Low Power Mode,
 * Android when "Remove animations" is on.
 *
 * Initialised from a lazy `useState` rather than an effect so the first paint
 * already respects it — a carousel that slides once and then starts fading is
 * worse than one that only ever faded.
 */
const usePrefersReducedMotion = (): boolean => {
  const [prefersReduced, setPrefersReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(QUERY).matches
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(QUERY);
    const onChange = () => setPrefersReduced(mediaQuery.matches);

    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  return prefersReduced;
};

export default usePrefersReducedMotion;
